import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './css/index.module.scss';
import SoundproofingServices from '../../../../services/SoundproofingServices';

const THUMB_WIDTH = 33;
const THUMBS = {
  min: 0,
  max: 1,
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function ValueInput({ value, onCommit }) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const number = Number(draft);
    if (draft === '' || Number.isNaN(number)) setDraft(String(value));
    else onCommit(number);
  };

  return (
    <input
      className={styles.valueInput}
      value={draft}
      inputMode="decimal"
      onChange={(ev) => setDraft(SoundproofingServices.sanitizeNumberInput(ev.target.value))}
      onBlur={commit}
      onKeyDown={(ev) => ev.key === 'Enter' && ev.target.blur()}
      style={{ width: `${Math.max(draft.length, 1) + 2}ch` }}
    />
  );
}

function RangeSlider({
  label,
  hint,
  min,
  max,
  step = 1,
  value,
  onChange,
  isDisabled,
}) {
  const trackRef = useRef(null);
  const [draggingThumb, setDraggingThumb] = useState(null);
  // Not rounded values while dragging, so the thumbs move smoothly
  const [liveValue, setLiveValue] = useState(null);
  const [minValue, maxValue] = liveValue || value;

  const getRatio = (val) => (max === min ? 0 : (val - min) / (max - min));
  const minRatio = getRatio(minValue);
  const maxRatio = getRatio(maxValue);

  const roundToStep = (val) => clamp(Math.round(val / step) * step, min, max);

  const getValueFromPointer = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left - THUMB_WIDTH / 2) / (rect.width - THUMB_WIDTH), 0, 1);
    return min + ratio * (max - min);
  };

  const changeThumbValue = (thumb, newValue, { isLive = false } = {}) => {
    const newPair = thumb === THUMBS.min
      ? [clamp(newValue, min, maxValue), maxValue]
      : [minValue, clamp(newValue, minValue, max)];
    const roundedPair = newPair.map(roundToStep);

    setLiveValue(isLive ? newPair : null);
    if (roundedPair[0] !== value[0] || roundedPair[1] !== value[1]) onChange(roundedPair);
  };

  const onTrackPointerDown = (ev) => {
    if (isDisabled) return;
    const newValue = getValueFromPointer(ev.clientX);
    const thumb = Math.abs(newValue - minValue) <= Math.abs(newValue - maxValue) && newValue <= maxValue
      ? THUMBS.min
      : THUMBS.max;

    trackRef.current.setPointerCapture(ev.pointerId);
    setDraggingThumb(thumb);
    changeThumbValue(thumb, newValue, { isLive: true });
  };

  const onTrackPointerMove = (ev) => {
    if (draggingThumb === null) return;
    const newValue = getValueFromPointer(ev.clientX);

    // Thumbs are at the same point: the one to move depends on the drag direction
    if (minValue === maxValue && newValue !== minValue) {
      const thumb = newValue > maxValue ? THUMBS.max : THUMBS.min;
      if (thumb !== draggingThumb) setDraggingThumb(thumb);
      return changeThumbValue(thumb, newValue, { isLive: true });
    }
    changeThumbValue(draggingThumb, newValue, { isLive: true });
  };

  const onTrackPointerUp = (ev) => {
    if (draggingThumb === null) return;
    trackRef.current.releasePointerCapture(ev.pointerId);
    setDraggingThumb(null);
    setLiveValue(null);
  };

  const getThumbLeft = (ratio) => `calc((100% - ${THUMB_WIDTH}px) * ${ratio})`;

  return (
    <div className={classNames(styles.card, { [styles.cardDisabled]: isDisabled })}>
      <div className={styles.header}>
        <div className={styles.label}>
          <p className={styles.labelText}>{label}</p>
          {hint}
        </div>
        <div className={styles.values}>
          <ValueInput value={value[0]} onCommit={(val) => changeThumbValue(THUMBS.min, val)} />
          <p className={styles.valuesDash}>–</p>
          <ValueInput value={value[1]} onCommit={(val) => changeThumbValue(THUMBS.max, val)} />
        </div>
      </div>

      <div
        ref={trackRef}
        className={classNames(styles.track, { [styles.trackAnimated]: draggingThumb === null }, 'noSelection')}
        onPointerDown={onTrackPointerDown}
        onPointerMove={onTrackPointerMove}
        onPointerUp={onTrackPointerUp}
        onPointerCancel={onTrackPointerUp}
      >
        <div
          className={styles.fill}
          style={{
            left: getThumbLeft(minRatio),
            width: `calc((100% - ${THUMB_WIDTH}px) * ${maxRatio - minRatio} + ${THUMB_WIDTH}px)`,
          }}
        />
        {[[THUMBS.min, value[0], minRatio], [THUMBS.max, value[1], maxRatio]].map(([thumb, thumbValue, ratio]) => (
          <div
            key={thumb}
            className={classNames(styles.thumb, { [styles.thumbActive]: draggingThumb === thumb })}
            style={{ left: getThumbLeft(ratio), width: `${THUMB_WIDTH}px` }}
          >
            <p className={styles.thumbValue}>{thumbValue}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default RangeSlider;
